import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Warnung } from '../types';
import './WarnungenList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const TYP_ICON: Record<string, string> = {
  MINDESTBESTAND:              'LAG',
  ZERTIFIKAT_ABLAUF:           'ZRT',
  AUFTRAG_UEBERFAELLIG:        '⏰',
  REKLAMATION_OFFEN:           'REK',
  FEUCHTIGKEITSGEHALT:         'FGT',
  RECHNUNG_UEBERFAELLIG:       'OPO',
  EINGANGSRECHNUNG_UNBEARBEITET: 'ER',
  UNBERECHTIGTE_AKTION:        'SEC',
};

const PRIO_COLOR: Record<string, string> = {
  HOCH:   'var(--red)',
  MITTEL: 'var(--gold)',
  INFO:   'var(--green-900)',
};

interface WarnungenListProps {
  authToken: string;
}

const WarnungenList: React.FC<WarnungenListProps> = ({ authToken }) => {
  const [warnungen, setWarnungen] = useState<Warnung[]>([]);
  const [loading, setLoading] = useState(true);
  const [nurUngelesen, setNurUngelesen] = useState(true);
  const [filterPrio, setFilterPrio] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/warnungen?nur_ungelesen=${nurUngelesen}&limit=100`,
        { headers }
      );
      setWarnungen(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nurUngelesen, authToken]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id: number) => {
    await axios.put(`${API_URL}/api/warnungen/${id}/lesen`, {}, { headers });
    setWarnungen(prev => prev.map(w => w.warnung_id === id ? { ...w, gelesen: true } : w));
  };

  const markAllRead = async () => {
    await axios.put(`${API_URL}/api/warnungen/alle-lesen`, {}, { headers });
    setWarnungen(prev => prev.map(w => ({ ...w, gelesen: true })));
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API_URL}/api/warnungen/refresh`, {}, { headers });
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });

  const sichtbar = warnungen.filter(w =>
    !filterPrio || w.prioritaet === filterPrio
  ).sort((a, b) => {
    const order: Record<string, number> = { HOCH: 0, MITTEL: 1, INFO: 2 };
    return (order[a.prioritaet] ?? 3) - (order[b.prioritaet] ?? 3);
  });

  const ungeleseneAnzahl = warnungen.filter(w => !w.gelesen).length;

  return (
    <div className="warnungen-list-container">
      <div className="wl-header">
        <h2>Warnungen</h2>
        <div className="wl-actions">
          <label className="wl-toggle">
            <input
              type="checkbox"
              checked={nurUngelesen}
              onChange={e => setNurUngelesen(e.target.checked)}
            />
            Nur ungelesen
          </label>
          <select
            className="wl-filter"
            value={filterPrio}
            onChange={e => setFilterPrio(e.target.value)}
          >
            <option value="">Alle Prioritäten</option>
            <option value="HOCH">Hoch</option>
            <option value="MITTEL">Mittel</option>
            <option value="INFO">Info</option>
          </select>
          {ungeleseneAnzahl > 0 && (
            <button className="wl-btn secondary" onClick={markAllRead}>
              Alle als gelesen ({ungeleseneAnzahl})
            </button>
          )}
          <button className="wl-btn primary" onClick={refresh} disabled={refreshing}>
            {refreshing ? 'Läuft...' : '↻ Checks jetzt'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="wl-loading">Lade Warnungen...</div>
      ) : sichtbar.length === 0 ? (
        <div className="wl-empty">
          <span>✓</span>
          <p>{nurUngelesen ? 'Keine ungelesenen Warnungen.' : 'Keine Warnungen vorhanden.'}</p>
        </div>
      ) : (
        <div className="wl-list">
          {sichtbar.map(w => (
            <div
              key={w.warnung_id}
              className={`wl-item ${w.gelesen ? 'gelesen' : ''}`}
            >
              <div className="wl-icon">{TYP_ICON[w.typ] || '—'}</div>
              <div className="wl-body">
                <div className="wl-titel">{w.titel}</div>
                <div className="wl-nachricht">{w.nachricht}</div>
                <div className="wl-meta">
                  <span
                    className="wl-prio-badge"
                    style={{ backgroundColor: PRIO_COLOR[w.prioritaet] }}
                  >
                    {w.prioritaet}
                  </span>
                  <span className="wl-datum">{formatDate(w.erstellt_am)}</span>
                  {w.referenz_typ && (
                    <span className="wl-ref">{w.referenz_typ} #{w.referenz_id}</span>
                  )}
                </div>
              </div>
              {!w.gelesen && (
                <button
                  className="wl-lesen-btn"
                  onClick={() => markRead(w.warnung_id)}
                  title="Als gelesen markieren"
                >
                  ✓ Gelesen
                </button>
              )}
              {w.gelesen && <span className="wl-gelesen-badge">✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WarnungenList;
